import React, { useEffect, useMemo } from "react";
import { connect } from "react-redux";

import FieldFeature from "@mapmycustomers/shared/enum/fieldModel/FieldFeature";
import FieldType from "@mapmycustomers/shared/enum/fieldModel/FieldType";
import { EntityTypesSupportedByMapsPage } from "@mapmycustomers/shared/types/entity";
import PinLegend from "@mapmycustomers/shared/types/map/PinLegend";

import useMapView from "@app/component/view/utils/useMapView";
import { getShapeLegends } from "@app/store/pinLegends/selectors";
import { RootState } from "@app/store/rootReducer";
import getFieldModelByEntityType from "@app/util/fieldModel/getByEntityType";
import loggingService from "@app/util/logging";
import { isSingleShapePinLegend } from "@app/util/pinLegend";

import CustomDateShapeKey from "./CustomDateShapeKey";
import CustomNumericShapeKey from "./CustomNumericShapeKey";
import CustomOptionShapeKey from "./CustomOptionShapeKey";
import FrequencyStatusShapeKey from "./FrequencyStatusShapeKey";
import NoShapeKey from "./NoShapeKey";
import SimpleShapeKey from "./SimpleShapeKey";

interface OwnProps {
  entityType: EntityTypesSupportedByMapsPage;
}

interface Props extends OwnProps {
  onSetTitle: (title?: string) => void;
  pinLegends: PinLegend[];
}

const EntityShapeKeyTab: React.FC<Props> = ({ entityType, onSetTitle, pinLegends }) => {
  const {
    viewState: { shapeKey },
  } = useMapView();

  const pinLegend = useMemo(() => {
    if (!shapeKey?.[entityType]) {
      return undefined;
    }
    return pinLegends.find(({ id }) => id === shapeKey[entityType]);
  }, [entityType, shapeKey, pinLegends]);

  useEffect(() => {
    onSetTitle(pinLegend?.name);
  }, [onSetTitle, pinLegend?.name]);

  if (isSingleShapePinLegend(pinLegend)) {
    return <SimpleShapeKey legend={pinLegend} />;
  } else if (pinLegend?.fieldName === "cadenceStatus") {
    // color by Cadence Status
    return <FrequencyStatusShapeKey />;
  } else if (pinLegend) {
    const fieldModel = getFieldModelByEntityType(pinLegend.entity);
    // FIXME: use esKey when BE is fixed, and even better - only use pinLegend.fieldName
    const fieldName = pinLegend.customField
      ? String(pinLegend.customField.id)
      : pinLegend.fieldName;
    const field = fieldModel.getByName(fieldName);
    if (!field) {
      loggingService.warning(
        `Received pin legend with invalid field: ${pinLegend.entity}.${fieldName}`,
        { field, pinLegend }
      );
      return null;
    }

    return field.hasFeature(FieldFeature.NUMERIC) ? (
      <CustomNumericShapeKey entityType={entityType} field={field} pinLegend={pinLegend} />
    ) : field.type === FieldType.DATE || field.type === FieldType.DATE_TIME ? (
      <CustomDateShapeKey entityType={entityType} pinLegend={pinLegend} />
    ) : (
      <CustomOptionShapeKey entityType={entityType} field={field} pinLegend={pinLegend} />
    );
  }

  // a default case:
  return <NoShapeKey />;
};

const mapStateToProps = (state: RootState, { entityType }: OwnProps) => ({
  pinLegends: getShapeLegends(state)(entityType),
});

export default connect(mapStateToProps)(EntityShapeKeyTab);
